// Shared renderer for the smart-page `file_upload` block. Reuses the same
// FileUploadField widget the transaction image/document columns use, so the
// attachment UX is identical across the app. The author-configured DOCUMENT
// TYPE and TRANSACTION ID (each a fixed value or a page variable) are resolved
// here and forwarded to the upload transport, so the file is stored under
// `<doc_type>/<transaction_id>/` for an archivable, per-record layout.
//
// Surface-agnostic: the actual upload transport is injected via `uploadFn`
// (in-app preview → makeEmbedUploader; deployed runtime → uploadTxnDocument),
// mirroring how the transaction form's uploader is injected.

import { useEffect, useRef, useState } from 'react'
import type { FileUploadBlock } from '@twasta/page-schema'
import { resolveValueRef } from './bindings'
import FileUploadField, { type UploadResult, type UploadMeta } from './txn/widgets/FileUploadField'

// Mirrors the transaction widget's document accept list (see txn/utils).
const DOC_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.odt,.ods,.json,.xml,image/*,application/pdf'

export interface FileUploadBlockRendererProps {
  block: FileUploadBlock
  vars: Record<string, unknown>
  setVar: (name: string, value: unknown) => void
  uploadFn: (file: File, meta?: UploadMeta) => Promise<UploadResult>
  // Runs a named PageAction after a successful upload (block.onUploadAction).
  // Injected per surface since the in-app preview and deployed runtime each
  // provide their own PageRuntime.
  runAction?: (name: string) => void | Promise<void>
  // Durable persistence of the uploaded URL into this block's own document
  // state (smart-doc instances). `persistedUrl` is the value restored on load;
  // `onPersist` writes it back. This makes an attachment survive save+reopen
  // even when no page variable is configured — and uses the reliable per-block
  // save path rather than the debounced page-variable path.
  persistedUrl?: string
  onPersist?: (url: string) => void
  disabled?: boolean
}

export function FileUploadBlockRenderer({
  block, vars, setVar, uploadFn, runAction, persistedUrl, onPersist, disabled,
}: FileUploadBlockRendererProps) {
  const kind = block.kind === 'image' ? 'image' : 'document'
  const accept = block.accept || (kind === 'image' ? 'image/*' : DOC_ACCEPT)

  // docType / transactionId resolve from a fixed value or a page variable.
  const docType = String(resolveValueRef(block.docType, { vars }) ?? '').trim()
  const transactionId = String(resolveValueRef(block.transactionId, { vars }) ?? '').trim()

  // Displayed URL, most-authoritative first: a bound page variable (if the
  // author wired one), else the value persisted in this block's document
  // state, else a same-session local fallback. The block-state value is what
  // makes the attachment durable across save + reopen with no var configured.
  const [localUrl, setLocalUrl] = useState('')
  const varUrl = block.valueVar ? String(vars[block.valueVar] ?? '') : ''
  const boundUrl = varUrl || persistedUrl || localUrl

  // Marks the URL produced by an actual upload so the on-upload action fires
  // only for user uploads — never for an initial seed (e.g. editing a record).
  const pendingUploadRef = useRef<string | null>(null)

  const onChange = (url: string) => {
    pendingUploadRef.current = url || null
    setLocalUrl(url)
    if (block.valueVar) setVar(block.valueVar, url)
    // Always persist into the block's own document state so the attachment is
    // restored on reopen regardless of whether a page variable is bound.
    onPersist?.(url)
  }

  // Fire the on-upload action once the new URL has committed to the var store,
  // so the action (reading {{valueVar}}) sees the uploaded URL.
  useEffect(() => {
    if (!block.onUploadAction || !runAction) return
    if (pendingUploadRef.current && pendingUploadRef.current === boundUrl) {
      pendingUploadRef.current = null
      void runAction(block.onUploadAction)
    }
  }, [boundUrl, block.onUploadAction, runAction])

  const missing: string[] = []
  if (!docType) missing.push('document type')
  if (!transactionId) missing.push('transaction id')

  return (
    <div>
      {block.label && (
        <div style={{
          fontSize: '0.8rem', fontWeight: 600, marginBottom: 6,
          color: 'var(--color-text, #111)',
        }}>
          {block.label}
        </div>
      )}
      <FileUploadField
        kind={kind}
        value={boundUrl}
        onChange={onChange}
        uploadFn={uploadFn}
        accept={accept}
        disabled={disabled || missing.length > 0}
        docType={docType || undefined}
        recordId={transactionId || undefined}
        previewMode={block.previewMode ?? 'auto'}
      />
      {missing.length > 0 && (
        <div style={{ color: '#b45309', fontSize: '0.75rem', marginTop: 4 }}>
          Set the {missing.join(' and ')} to enable uploads.
        </div>
      )}
    </div>
  )
}

export default FileUploadBlockRenderer
